import { useEffect, useRef, useState, type FormEvent } from "react";
import { Plus, Settings2, Trash2, X } from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, Field } from "../../ui";

type Building = { id: string; name: string };
type ProjectType = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  categoryId: string;
  categoryCode: "A" | "B" | "C";
};
type DocumentType = {
  id: string;
  code: string;
  name: string;
  category: string;
};
type Requirement = {
  id: string;
  documentTypeId: string;
  documentTypeCode: string;
  documentTypeName: string;
  requiredFrom: string;
  isRequired: boolean;
  condition: { flag: string; equals: boolean } | null;
};

const PARTIES = [
  "owner",
  "general_contractor",
  "plumber",
  "electrician",
  "hvac",
] as const;

const FLAGS = [
  "requiresRiserShutdown",
  "requiresTelecomShutdown",
  "requiresIndemnityDeviation",
  "requiresLicenseAgreement",
  "isEmergency",
] as const;

export function AdminProjectTypes() {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState<string>("");
  const [types, setTypes] = useState<ProjectType[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const requirementsRef = useRef<HTMLDivElement>(null);

  const [newType, setNewType] = useState({
    code: "",
    name: "",
    description: "",
    categoryCode: "B" as "A" | "B" | "C",
  });

  const [newReq, setNewReq] = useState({
    documentTypeCode: "",
    requiredFrom: "owner" as (typeof PARTIES)[number],
    useCondition: false,
    flag: FLAGS[0] as (typeof FLAGS)[number],
    equals: true,
  });

  useEffect(() => {
    void api.get<{ data: Building[] }>("/api/buildings").then((r) => {
      setBuildings(r.data);
      if (r.data[0]) setBuildingId(r.data[0].id);
    });
  }, []);

  const reloadTypes = async () => {
    if (!buildingId) return;
    const r = await api.get<{ data: ProjectType[] }>(
      `/api/buildings/${buildingId}/project-types`,
    );
    setTypes(r.data);
    // Don't auto-select on load — wait for the user to click "Manage…"
    // so the requirements panel doesn't appear unbidden below the table.
    if (selectedTypeId && !r.data.find((t) => t.id === selectedTypeId)) {
      setSelectedTypeId(null);
    }
  };

  const onManage = (id: string) => {
    setSelectedTypeId(id);
    // Scroll the requirements panel into view on the next paint so the
    // user sees the result of their click immediately. Without this, on
    // pages with many project types the panel renders below the fold
    // and the click feels unresponsive.
    requestAnimationFrame(() => {
      requirementsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const reloadDocTypes = async () => {
    if (!buildingId || types.length === 0) return;
    const r = await api
      .get<{
        data: {
          documentTypeCode: string;
          documentTypeName: string;
          documentTypeCategory?: string;
        }[];
      }>(`/api/project-types/${types[0]!.id}/requirements`)
      .catch(() => ({ data: [] }));
    const seen = new Map<string, DocumentType>();
    for (const row of r.data) {
      seen.set(row.documentTypeCode, {
        id: row.documentTypeCode,
        code: row.documentTypeCode,
        name: row.documentTypeName,
        category: row.documentTypeCategory ?? "",
      });
    }
    setDocTypes(
      Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name)),
    );
  };

  const reloadRequirements = async () => {
    if (!selectedTypeId) {
      setRequirements([]);
      return;
    }
    const r = await api.get<{ data: Requirement[] }>(
      `/api/admin/project-types/${selectedTypeId}/requirements`,
    );
    setRequirements(r.data);
  };

  useEffect(() => {
    if (buildingId) void reloadTypes();
  }, [buildingId]);
  useEffect(() => {
    if (types.length > 0) void reloadDocTypes();
  }, [types.length]);
  useEffect(() => {
    void reloadRequirements();
  }, [selectedTypeId]);

  const createType = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post(
        `/api/admin/buildings/${buildingId}/project-types`,
        newType,
      );
      setNewType({ code: "", name: "", description: "", categoryCode: "B" });
      await reloadTypes();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : String(err));
    }
  };

  const toggleActive = async (t: ProjectType) => {
    await api.patch(`/api/admin/project-types/${t.id}`, {
      isActive: !t.isActive,
    });
    await reloadTypes();
  };

  const renameType = async (t: ProjectType, name: string) => {
    if (name === t.name) return;
    await api.patch(`/api/admin/project-types/${t.id}`, { name });
    await reloadTypes();
  };

  const addReq = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedTypeId) return;
    setError(null);
    try {
      await api.post(`/api/admin/project-types/${selectedTypeId}/requirements`, {
        documentTypeCode: newReq.documentTypeCode,
        requiredFrom: newReq.requiredFrom,
        condition: newReq.useCondition
          ? { flag: newReq.flag, equals: newReq.equals }
          : null,
      });
      setNewReq({ ...newReq, documentTypeCode: "" });
      await reloadRequirements();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : String(err));
    }
  };

  const removeReq = async (id: string) => {
    await api.del(`/api/admin/project-type-requirements/${id}`);
    await reloadRequirements();
  };

  const selectedType = types.find((t) => t.id === selectedTypeId) ?? null;

  return (
    <div>
      <Card className="!p-0 overflow-hidden">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-ink-100">
          <h2 className="text-ink-800 m-0">Project types</h2>
          <select
            className="select max-w-xs"
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
          >
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        {types.length === 0 ? (
          <p className="p-6 text-ink-500 text-sm">No project types yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Cat</th>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr
                    key={t.id}
                    className={t.id === selectedTypeId ? "bg-brand-50" : ""}
                  >
                    <td>
                      <span className="badge bg-ink-100 text-ink-700">
                        {t.categoryCode}
                      </span>
                    </td>
                    <td>
                      <code>{t.code}</code>
                    </td>
                    <td>
                      <input
                        className="input"
                        defaultValue={t.name}
                        onBlur={(e) => void renameType(t, e.target.value)}
                      />
                    </td>
                    <td>
                      <Button size="sm" onClick={() => void toggleActive(t)}>
                        {t.isActive ? "Active" : "Disabled"}
                      </Button>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant={
                          t.id === selectedTypeId ? "primary" : "secondary"
                        }
                        onClick={() => onManage(t.id)}
                      >
                        <Settings2 size={14} />
                        {t.id === selectedTypeId
                          ? "Managing"
                          : "Manage requirements"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-3">Add a project type</h3>
        <form onSubmit={createType}>
          <div className="field-row">
            <Field label="Category">
              <select
                className="select"
                value={newType.categoryCode}
                onChange={(e) =>
                  setNewType({
                    ...newType,
                    categoryCode: e.target.value as "A" | "B" | "C",
                  })
                }
              >
                <option value="A">A — Decorative</option>
                <option value="B">B — System-Affecting</option>
                <option value="C">C — Common Element / Structural</option>
              </select>
            </Field>
            <Field label="Code (lower_snake)">
              <input
                className="input"
                value={newType.code}
                onChange={(e) =>
                  setNewType({ ...newType, code: e.target.value })
                }
                required
                pattern="[a-z0-9_]+"
              />
            </Field>
            <Field label="Name" className="flex-[2]">
              <input
                className="input"
                value={newType.name}
                onChange={(e) =>
                  setNewType({ ...newType, name: e.target.value })
                }
                required
              />
            </Field>
          </div>
          <Field label="Description">
            <textarea
              className="textarea"
              rows={2}
              value={newType.description}
              onChange={(e) =>
                setNewType({ ...newType, description: e.target.value })
              }
            />
          </Field>
          {error && <div className="alert-error">{error}</div>}
          <Button variant="primary" type="submit">
            <Plus size={16} /> Add project type
          </Button>
        </form>
      </Card>

      {selectedType && (
        <div ref={requirementsRef} className="scroll-mt-20">
          <Card>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-ink-800 m-0">
                  Requirements for{" "}
                  <span className="text-brand-700">{selectedType.name}</span>
                </h3>
                <p className="text-xs text-ink-500 mt-1">
                  <code>{selectedType.code}</code> · Cat{" "}
                  {selectedType.categoryCode}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedTypeId(null)}
                aria-label="Close requirements panel"
              >
                <X size={14} /> Close
              </Button>
            </div>
          {requirements.length === 0 ? (
            <p className="text-ink-500 text-sm">No requirements configured.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Required from</th>
                    <th>Condition</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {requirements.map((r) => (
                    <tr key={r.id}>
                      <td className="font-medium text-ink-800">
                        {r.documentTypeName}
                      </td>
                      <td>
                        <code>{r.requiredFrom}</code>
                      </td>
                      <td>
                        {r.condition ? (
                          <span className="text-xs text-ink-600">
                            {r.condition.flag} = {String(r.condition.equals)}
                          </span>
                        ) : (
                          <span className="text-xs text-ink-400">always</span>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void removeReq(r.id)}
                        >
                          <Trash2 size={14} /> Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form onSubmit={addReq} className="mt-4">
            <h4 className="text-ink-800 mb-2 font-semibold">Add a requirement</h4>
            <div className="field-row">
              <Field label="Document type code" className="flex-[2]">
                <input
                  className="input"
                  list="dt-list"
                  value={newReq.documentTypeCode}
                  onChange={(e) =>
                    setNewReq({ ...newReq, documentTypeCode: e.target.value })
                  }
                  required
                />
                <datalist id="dt-list">
                  {docTypes.map((d) => (
                    <option key={d.code} value={d.code}>
                      {d.name}
                    </option>
                  ))}
                </datalist>
              </Field>
              <Field label="Required from">
                <select
                  className="select"
                  value={newReq.requiredFrom}
                  onChange={(e) =>
                    setNewReq({
                      ...newReq,
                      requiredFrom: e.target.value as (typeof PARTIES)[number],
                    })
                  }
                >
                  {PARTIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-700 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={newReq.useCondition}
                onChange={(e) =>
                  setNewReq({ ...newReq, useCondition: e.target.checked })
                }
                className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
              />
              Conditional requirement
            </label>
            {newReq.useCondition && (
              <div className="field-row">
                <Field label="Flag" className="flex-[2]">
                  <select
                    className="select"
                    value={newReq.flag}
                    onChange={(e) =>
                      setNewReq({
                        ...newReq,
                        flag: e.target.value as (typeof FLAGS)[number],
                      })
                    }
                  >
                    {FLAGS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Equals">
                  <select
                    className="select"
                    value={newReq.equals ? "true" : "false"}
                    onChange={(e) =>
                      setNewReq({
                        ...newReq,
                        equals: e.target.value === "true",
                      })
                    }
                  >
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </Field>
              </div>
            )}
            <Button variant="primary" type="submit">
              <Plus size={16} /> Add requirement
            </Button>
          </form>
          </Card>
        </div>
      )}
    </div>
  );
}
